import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, Button, Box, Grid, FormControl, TextField, Autocomplete, FormLabel,
} from '@mui/material';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import ProjectSelector from '../../components/selectors/ProjectSelector';

const FORM_DEFAULT = {
  email: '', accountType: '', department: '', designation: '', selectedProjects: [],
};

const deparments = [
  { label: 'Others' },
  { label: 'Contractors' },
  { label: 'Product Development' },
  { label: 'Development' },
  { label: 'BPO' },
  { label: 'Izenda' },
  { label: 'ChartRequest' },
  { label: 'Full Cycle' },
];

const designations = [
  { label: 'Others' },
  { label: 'Software Developer' },
  { label: 'Senior Data Scientist' },
  { label: 'Team Lead' },
  { label: 'Business Process Analyst' },
  { label: 'BI Analyst' },
  { label: 'Customer Success Associate' },
  { label: 'Software Developer II' },
  { label: 'QA Lead' },
  { label: 'Business Analyst' },
  { label: 'Junior Software Developer' },
  { label: 'Senior Business Analyst' },
  { label: 'QA Manager' },
  { label: 'HR' },
];

const accountTypes = [
  { label: 'Team member' },
  { label: 'Team Leader' },
  { label: 'Workspace owner' },
  { label: 'Workspace admin' },
];

function NewMemberModal({ closeModal, getMembers, reportingManagers }) {
  const { activeWorkspace } = useContext(UserContext);
  const [formData, setFormData] = useState(FORM_DEFAULT);
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState();
  const [projects, setProjects] = useState([]);
  // eslint-disable-next-line
  const [projectsLoading, setProjectsLoading] = useState(false);

  const handleForm = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleProjectSelection = (selectedProjects) => {
    setFormData({ ...formData, selectedProjects });
  };

  const handleAddMember = async () => {
    setFormErrors({});

    try {
      const memberResponse = await sendRequest({
        method: 'post',
        url: API.WORKSPACE.member(activeWorkspace.id),
        data: {
          email: formData.email,
          accountType: formData.accountType,
          department: formData.department,
          designation: formData.designation,
          reportingManager: formData.reportingManager,
        },
      });

      if (memberResponse.success) {
        const newMemberId = memberResponse.data.user.id;
        if (formData.selectedProjects.length > 0 && newMemberId) {
          const projectIds = formData.selectedProjects.map((project) => project.id);
          await sendRequest({
            method: 'post',
            url: API.WORKSPACE.addToProjects(activeWorkspace.id, newMemberId),
            data: { projectIds },
          });
        }

        setSuccessMessage(`Created new member. Invitation sent to ${formData.email}`);
        setFormData(FORM_DEFAULT);
        getMembers();
        closeModal();
      } else {
        setFormErrors(memberResponse.data);
      }
    } catch (error) {
      console.error('Error adding member:', error);
    }
  };

  useEffect(() => {
    const getProjects = async () => {
      if (activeWorkspace) {
        setProjectsLoading(true);
        try {
          const response = await sendRequest({
            method: 'get',
            url: API.WORKSPACE.getProjects(activeWorkspace.id),
          });

          if (response.success && response.data?.data && Array.isArray(response.data.data)) {
            const projectsArray = response.data.data;

            const formattedProjects = projectsArray.map((project) => ({
              id: project.id,
              name: project.name || `Project ${project.id}`,
              description: project.description || '',
              workspaceId: project.workspaceId,
              ...project,
            }));

            setProjects(formattedProjects);
          } else {
            setProjects([]);
          }
        } catch (error) {
          setProjects([]);
        } finally {
          setProjectsLoading(false);
        }
      }
    };

    getProjects();
  }, [activeWorkspace]);

  return (
    <Modal open onClose={closeModal} className="flex-row align-c jstfy-c" data-testid="new-member-modal">
      <Box style={{
        width: 588,
        padding: 32,
        background: '#fff',
        borderRadius: 8,
      }}
      >
        <h5 className="m-b16 text-600" data-testid="modal-title">Add a New Workspace Member</h5>
        <Grid container rowSpacing={3} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>

          {/* Email Field */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel sx={{ color: '#293855', fontSize: '14px', fontWeight: '500' }}>
                Email Address
              </FormLabel>
              <TextField
                required
                name="email"
                placeholder="Required"
                value={formData?.email}
                onChange={(event) => handleForm(event.target.name, event.target.value)}
                type="email"
                error={!!formErrors?.email}
                helperText={formErrors?.email}
                data-testid="email"
                inputProps={{
                  sx: { '&::placeholder': { color: '#7D8698' } },
                }}
              />
            </FormControl>
          </Grid>

          {/* Account Type Field */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel sx={{ color: '#293855', fontSize: '14px', fontWeight: '500' }}>
                Account Type
              </FormLabel>
              <Autocomplete
                disablePortal
                id="combo-box-accountTypes"
                name="accountType"
                onInputChange={(event, newInputValue) => {
                  handleForm('accountType', newInputValue);
                }}
                options={accountTypes}
                renderInput={(params) => (
                  <TextField
                  // eslint-disable-next-line
                    {...params}
                    value={formData.accountType}
                    placeholder="Required"
                    data-testid="account-type"
                  />
                )}
              />
            </FormControl>
          </Grid>

          {/* Reporting Manager Field */}
          <Grid item xs={12}>
            <FormControl sx={{ width: '100%' }}>
              <Autocomplete
                value={reportingManagers.find((m) => m.id === formData.reportingManager) || null}
                onChange={(e, newValue) => {
                  setFormData({ ...formData, reportingManager: newValue?.id || '' });
                }}
                options={reportingManagers}
                getOptionLabel={(option) => (`${option.user.firstName} ${option.user.lastName}`) || ''}
                renderInput={(params) => (
                  <TextField
                  // eslint-disable-next-line
                    {...params}
                    label="Reporting To"
                    variant="outlined"
                  />
                )}
              />
            </FormControl>
          </Grid>

          {/* Department Field */}
          <Grid item xs={6}>
            <FormControl fullWidth>
              <FormLabel sx={{ color: '#293855', fontSize: '14px', fontWeight: '500' }}>
                Department
              </FormLabel>
              <Autocomplete
                disablePortal
                id="combo-box-departments"
                name="department"
                onInputChange={(event, newInputValue) => {
                  handleForm('department', newInputValue);
                }}
                options={deparments}
                sx={{ width: 254 }}
                renderInput={(params) => (
                  <TextField
                  // eslint-disable-next-line
                    {...params}
                    name="department"
                    value={formData.department}
                    placeholder="Department"
                    data-testid="department"
                  />
                )}
              />
            </FormControl>
          </Grid>

          {/* Designation Field */}
          <Grid item xs={6}>
            <FormControl fullWidth>
              <FormLabel sx={{ color: '#293855', fontSize: '14px', fontWeight: '500' }}>
                Designation
              </FormLabel>
              <Autocomplete
                disablePortal
                id="combo-box-designations"
                name="designation"
                onInputChange={(event, newInputValue) => {
                  handleForm('designation', newInputValue);
                }}
                options={designations}
                sx={{ width: 254 }}
                renderInput={(params) => (
                  <TextField
                  // eslint-disable-next-line
                    {...params}
                    name="designation"
                    value={formData.designation}
                    placeholder="Designation"
                    data-testid="designation"
                  />
                )}
              />
            </FormControl>
          </Grid>

          {/* NEW: Project Selection Field */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <ProjectSelector
                label="Projects"
                options={projects}
                selected={formData.selectedProjects}
                onChange={handleProjectSelection}
                placeholder={projects.length === 0 ? 'No projects available' : 'Select projects...'}
              />
            </FormControl>
          </Grid>

        </Grid>

        {/* Action Buttons */}
        <div className="newMemberForm-btns" style={{ marginTop: 24 }}>
          <Button
            onClick={closeModal}
            color="secondary"
            sx={{
              marginRight: 2,
              border: '1px solid #7D8698',
              width: 85,
              textTransform: 'none',
            }}
            data-testid="cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddMember}
            sx={{
              width: 131,
              textTransform: 'none',
            }}
            disabled={!(formData?.email && formData?.accountType)}
            data-testid="add-member"
          >
            Add Member
          </Button>
        </div>

        {/* Success/Error Messages */}
        {formErrors.general && (
          <div className="error-msg" style={{ color: 'red', marginTop: 10 }}>
            {formErrors.general}
          </div>
        )}
        <div className="success-msg">{successMessage}</div>
      </Box>
    </Modal>
  );
}

NewMemberModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  getMembers: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  reportingManagers: PropTypes.array.isRequired,
};

export default NewMemberModal;
